import { useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Stepper } from '../components/onboarding/Stepper'
import { StepConnect } from '../components/onboarding/StepConnect'
import { StepCategories } from '../components/onboarding/StepCategories'
import { StepSync } from '../components/onboarding/StepSync'
import { StepProcess } from '../components/onboarding/StepProcess'

const STEP_LABELS = ['Connect mailbox', 'Categories', 'Sync emails', 'Process & analyse']

function markDone(userId) {
  if (!userId) return
  try { localStorage.setItem(`onboarding_done_${userId}`, '1') } catch { /* ignore */ }
}

export function OnboardingPage() {
  const { socket } = useOutletContext()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [step, setStep] = useState(0)
  const [accountId, setAccountId] = useState(null)
  const [syncDone, setSyncDone] = useState(false)

  const next = () => setStep((s) => Math.min(s + 1, STEP_LABELS.length - 1))
  const back = () => setStep((s) => Math.max(s - 1, 0))

  const skipOnboarding = () => {
    markDone(user?.id)
    navigate('/')
  }

  const finish = () => {
    markDone(user?.id)
    navigate('/contacts')
  }

  return (
    <div className="onboarding-page">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <h1>Welcome — let&apos;s set things up</h1>
          <p>Four quick steps to go from an empty account to analysed contacts.</p>
        </div>

        <Stepper steps={STEP_LABELS} current={step} />

        {step === 0 && (
          <StepConnect
            onAccountCreated={(account) => {
              setAccountId(account?._id || account?.id)
              setTimeout(() => next(), 600)
            }}
          />
        )}

        {step === 1 && <StepCategories accountId={accountId} />}

        {step === 2 && (
          <StepSync
            accountId={accountId}
            socket={socket}
            onComplete={() => setSyncDone(true)}
          />
        )}

        {step === 3 && <StepProcess socket={socket} onAllDone={() => { /* user clicks finish */ }} />}

        <div className="onboarding-actions">
          <button type="button" className="onboarding-skip-link" onClick={skipOnboarding}>
            Skip onboarding
          </button>
          <div className="onboarding-actions-right">
            {step > 0 && (
              <button type="button" className="btn btn-ghost" onClick={back}>Back</button>
            )}
            {step < STEP_LABELS.length - 1 && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={next}
                disabled={
                  (step === 0 && !accountId) ||
                  (step === 2 && !syncDone)
                }
                title={
                  step === 0 && !accountId ? 'Add an account first'
                  : step === 2 && !syncDone ? 'Wait for sync to complete (or run in background)'
                  : ''
                }
              >
                {step === 2 && !syncDone ? 'Waiting for sync…' : 'Continue'}
              </button>
            )}
            {step === 2 && !syncDone && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { setSyncDone(true); next() }}
                title="Continue while sync keeps running in the background"
              >
                Run in background &amp; continue
              </button>
            )}
            {step === STEP_LABELS.length - 1 && (
              <button type="button" className="btn btn-primary" onClick={finish}>
                View your contacts
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
